import type { components, operations } from './generated/office'
import type { components as gateway, operations as gatewayOps } from './generated/core-gateway'

/** Recursively strips `readonly` so generated types are compatible with Immer's WritableDraft.
 * TODO: Investigate if this is necessary or should be done some other way
 */
export type Mutable<T> = { -readonly [K in keyof T]: Mutable<T[K]> }

/**
 * Friendly aliases over the generated schemas. This is the only file allowed to index
 * into `generated/` — see the no-restricted-imports rule in .eslintrc.js. When the
 * backend renames a schema, the fix lands here once instead of at every call site.
 *
 * Two specs feed this: the office API the frontend calls, and the CoreV2 public gateway
 * that the office API's /api/2/* endpoints proxy. The office spec documents no response
 * body for those proxies, so their payload types come from the gateway spec.
 */

/** 200 response body of an operation, e.g. `Ok<'get_office_get_countries'>`. */
export type Ok<O extends keyof operations> = operations[O] extends {
  responses: { 200: { content: { 'application/json': infer T } } }
}
  ? T
  : never

/** JSON request body of an operation. */
export type Body<O extends keyof operations> = operations[O] extends {
  requestBody?: { content: { 'application/json': infer T } }
}
  ? T
  : never

/** Path parameters of an operation. */
export type PathParams<O extends keyof operations> = operations[O]['parameters'] extends { path?: infer P } ? P : never

/** Query parameters of an operation. */
export type QueryParams<O extends keyof operations> = operations[O]['parameters'] extends { query?: infer Q }
  ? Q
  : never

// Response models. Names match the spec's schema names so a spec diff maps
// straight onto a diff here.
export type ChallengeResponse = components['schemas']['ChallengeResponse']
export type ClubDetailResponse = components['schemas']['ClubDetailResponse']
export type ClubResponse = components['schemas']['ClubResponse']
export type ConfigurationResponse = components['schemas']['ConfigurationResponse']
export type CountryResponse = components['schemas']['CountryResponse']
export type CourseDetailResponse = components['schemas']['CourseDetailResponse']
export type PrivilegeResponse = components['schemas']['PrivilegeResponse']
export type ProviderResponse = components['schemas']['ProviderResponse']
export type SiteConfigResponse = components['schemas']['SiteConfigResponse']
export type StateResponse = components['schemas']['StateResponse']
export type SuccessResponse = components['schemas']['SuccessResponse']
export type TimeZoneResponse = components['schemas']['TimeZoneResponse']
export type UserResponse = components['schemas']['UserResponse']

// CoreV2 public gateway. Every response is enveloped in `{ data, paging? }`, so the
// aliases below name both the envelope (what an endpoint returns) and the payload
// (what components actually work with).
//
// The generated gateway types are deeply `readonly`. Wrapping with `Mutable` at this
// layer keeps slice state and Immer's WritableDraft happy without per-file casts.
export type ChallengeFullDetailData = Mutable<gateway['schemas']['ChallengeFullDetailData']>
export type ChallengeInfo = Mutable<gateway['schemas']['ChallengeInfo']>
export type ChallengeSchedule = Mutable<gateway['schemas']['ChallengeSchedule']>
export type ChallengeTypeInfo = Mutable<gateway['schemas']['ChallengeTypeInfo']>
export type LeaderboardEntry = Mutable<gateway['schemas']['LeaderboardEntry']>
export type Participant = Mutable<gateway['schemas']['Participant']>
export type UserChallenge = Mutable<gateway['schemas']['UserChallenge']>
export type AudienceControl = Mutable<gateway['schemas']['AudienceControl']>
export type AudienceTargeting = Mutable<gateway['schemas']['AudienceTargeting']>
export type Terms = Mutable<gateway['schemas']['ChallengeTerms']>

export type EligibilityReason = Mutable<gateway['schemas']['EligibilityReason']>
export type UserChallengeRound = Mutable<gateway['schemas']['UserChallengeRound']>

// Enum types extracted from core-gateway operations for use in API function signatures.
export type ChallengeImageType =
  gatewayOps['ChallengesMediaPublicGatewayService_GetImage']['parameters']['path']['challengeImageType']
export const ChallengeImageType = {
  UNSPECIFIED: 'CHALLENGE_IMAGE_TYPE_UNSPECIFIED',
  COVER: 'CHALLENGE_IMAGE_TYPE_COVER',
  HIGHLIGHT: 'CHALLENGE_IMAGE_TYPE_HIGHLIGHT',
  BANNER: 'CHALLENGE_IMAGE_TYPE_BANNER',
  LOGO: 'CHALLENGE_IMAGE_TYPE_LOGO',
  BACKGROUND: 'CHALLENGE_IMAGE_TYPE_BACKGROUND',
  TERMS: 'CHALLENGE_IMAGE_TYPE_TERMS',
} as const satisfies Record<string, ChallengeImageType>
export type ChallengeCategory = NonNullable<gateway['schemas']['ChallengeFullDetailData']['category']>

export const ChallengeCategory = {
  UNSPECIFIED: 'CHALLENGE_CATEGORY_UNSPECIFIED',
  SPONSORED: 'CHALLENGE_CATEGORY_SPONSORED',
  USER: 'CHALLENGE_CATEGORY_USER',
} as const satisfies Record<string, ChallengeCategory>

export const GenderType = {
  MALE: 'GOLF_GENDER_MALE',
  FEMALE: 'GOLF_GENDER_FEMALE',
} as const satisfies Record<string, NonNullable<gateway['schemas']['AudienceTargeting']['gender']>>

export type ChallengeTypeConfigType = NonNullable<gateway['schemas']['ChallengeTypeConfig']['type']>
export const ChallengeTypeConfig = {
  UNSPECIFIED: 'CHALLENGE_TYPE_UNSPECIFIED',
  NUMBER_OF_HOLES_PLAYED: 'CHALLENGE_TYPE_NUMBER_OF_HOLES_PLAYED',
  NUMBER_OF_COURSES_PLAYED: 'CHALLENGE_TYPE_NUMBER_OF_COURSES_PLAYED',
  NUMBER_OF_PARS: 'CHALLENGE_TYPE_NUMBER_OF_PARS',
  BIRDIES_OR_BETTER: 'CHALLENGE_TYPE_BIRDIES_OR_BETTER',
  STROKEPLAY: 'CHALLENGE_TYPE_STROKEPLAY',
  STABLEFORD: 'CHALLENGE_TYPE_STABLEFORD',
  ECLECTIC: 'CHALLENGE_TYPE_ECLECTIC',
} as const satisfies Record<string, NonNullable<gateway['schemas']['ChallengeTypeConfig']['type']>>

export type AddParticipantsResponse = Mutable<gateway['schemas']['AddParticipantsResponse']>
export type CloneChallengeResponse = Mutable<gateway['schemas']['CloneChallengeResponse']>
export type ConfirmImageUploadResponse = Mutable<gateway['schemas']['ConfirmImageUploadResponse']>
export type CreateChallengeResponse = Mutable<gateway['schemas']['CreateChallengeResponse']>
export type DeleteChallengeResponse = Mutable<gateway['schemas']['DeleteChallengeResponse']>
export type DeleteImageResponse = Mutable<gateway['schemas']['DeleteImageResponse']>
export type GetChallengeInsightsResponse = Mutable<gateway['schemas']['GetChallengeInsightsResponse']>
export type GetChallengeResponse = Mutable<gateway['schemas']['GetChallengeResponse']>
export type GetChallengeTypesResponse = Mutable<gateway['schemas']['GetChallengeTypesResponse']>
export type GetImageResponse = Mutable<gateway['schemas']['GetImageResponse']>
export type GetLeaderboardResponse = Mutable<gateway['schemas']['GetLeaderboardResponse']>
export type GetUserChallengeRoundsResponse = Mutable<gateway['schemas']['GetUserChallengeRoundsResponse']>
export type ListChallengesResponse = Mutable<gateway['schemas']['ListChallengesResponse']>
export type ListUserChallengesResponse = Mutable<gateway['schemas']['ListUserChallengesResponse']>
export type MediaUploadData = Mutable<gateway['schemas']['MediaUploadData']>
export type OverrideRoundEligibilityResponse = Mutable<gateway['schemas']['OverrideRoundEligibilityResponse']>
export type RecalculateChallengeResponse = Mutable<gateway['schemas']['RecalculateChallengeResponse']>
export type RegenerateInviteTokenResponse = Mutable<gateway['schemas']['RegenerateInviteTokenResponse']>
export type RemoveParticipantResponse = Mutable<gateway['schemas']['RemoveParticipantResponse']>
export type RequestImageUploadResponse = Mutable<gateway['schemas']['RequestImageUploadResponse']>
export type TransitionChallengeStateResponse = Mutable<gateway['schemas']['TransitionChallengeStateResponse']>
export type TransitionParticipantStateResponse = Mutable<gateway['schemas']['TransitionParticipantStateResponse']>
export type UpdateChallengeResponse = Mutable<gateway['schemas']['UpdateChallengeResponse']>

// Request payloads the gateway accepts, for anything posted through an /api/2 proxy.
export type ChallengeCreateAndUpdateData = Mutable<gateway['schemas']['ChallengeCreateAndUpdateData']>
export type AddParticipantsPayload = Mutable<gateway['schemas']['AddParticipantsPayload']>
export type OverrideRoundEligibilityPayload = Mutable<gateway['schemas']['OverrideRoundEligibilityPayload']>
export type TransitionChallengeStatePayload = Mutable<gateway['schemas']['TransitionChallengeStatePayload']>
export type UpdateChallengePayload = Mutable<gateway['schemas']['UpdateChallengePayload']>

// CoreV2 models the office API declares in its own spec (the /api/1 endpoints).
export type ContestCoreV2Model = components['schemas']['ContestCoreV2Model']
export type GameCoreV2Model = components['schemas']['GameCoreV2Model']
export type PlayerCoreV2Model = components['schemas']['PlayerCoreV2Model']
export type RoundCoreV2Model = components['schemas']['RoundCoreV2Model']
export type UserPrivilegeCoreV2Model = components['schemas']['UserPrivilegeCoreV2Model']

// Request bodies, typed off the operation rather than the schema so a form can never
// drift from the endpoint it posts to.
export type ChallengeSaveBody = Body<'post_office_create_new_challenge'>
export type ClubSaveBody = Body<'post_office_add_club'>
export type CourseSearchBody = Body<'post_office_search_courses'>
